// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

// --- HS Code
import {
  SortDirections,
  SurveyStatuses,
  SurveySummaryModel,
} from '../../../models';
import { ResponseBaseService } from '../survey/response-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class RespondentSurveyBaseService {
  protected _surveyList$: BehaviorSubject<SurveySummaryModel[]>;
  get surveyList$(): Observable<SurveySummaryModel[]> {
    return this._surveyList$;
  }

  protected _selectedSurvey$: BehaviorSubject<SurveySummaryModel | null>;
  get selectedSurvey$(): Observable<SurveySummaryModel | null> {
    return this._selectedSurvey$;
  }

  filteredSurveyList$: Observable<SurveySummaryModel[]>;
  numberOfIncompleteSurveys$: Observable<number>;
  numberOfStartedSurveys$: Observable<number>;

  constructor(protected responseService: ResponseBaseService) {
    this._surveyList$ = new BehaviorSubject<SurveySummaryModel[]>([]);
    this._selectedSurvey$ = new BehaviorSubject<SurveySummaryModel | null>(null);

    this.filteredSurveyList$ = combineLatest([
      this.surveyList$,
      this.selectedSurvey$,
    ]).pipe(
      map(([surveys, selectedSurvey]) => {
        let filtered = surveys;
        //Task #13831 - Preventing the full list after coming back from survey
        // if below code is commented, it introduces filter not working error #14433
        if (selectedSurvey) {
          filtered = surveys.filter(
            (s) => s.campaignId === selectedSurvey.campaignId
          );
        }
        return filtered;
      })
    );

    this.numberOfIncompleteSurveys$ = this.filteredSurveyList$.pipe(
      map((filteredList) => {
        if (!Array.isArray(filteredList)) {
          return 0;
        }
        return filteredList.filter(
          (f) => f.status < SurveyStatuses.completeSurvey && !f.isLocked
        ).length;
      })
    );

    this.numberOfStartedSurveys$ = this.filteredSurveyList$.pipe(
      map((filteredList) => {
        if (!Array.isArray(filteredList)) {
          return 0;
        }
        return filteredList.filter(
          (f) => f.status >= SurveyStatuses.indicatorQuestions || f.isLocked
        ).length;
      })
    );

    this.responseService.activeResponse$.subscribe((response) => {
      if (!response || !this._surveyList$.value) {
        return;
      }

      const surveys = this._surveyList$.value;

      const activeSurvey = surveys.find((s) => s.id === response.id);
      if (activeSurvey) {
        activeSurvey.status = response.status;
        activeSurvey.updated = new Date();
      }
      this._surveyList$.next(surveys);
    });
  }

  abstract loadSummary(
    pageSize: number,
    pageNumber: number,
    filter: string,
    sortItem: string,
    sortDirection: SortDirections
  ): void;
  abstract loadSurveyById(id: string): void;

  selectSurvey(identifier: string | null): void {
    this._selectedSurvey$.next(this.getSurveyById(identifier));
  }

  protected getSurveyById(identifier: string | null): SurveySummaryModel | null {
    if (!identifier) return null;

    const summaries = this._surveyList$.value;
    if (summaries && summaries.length) {
      const summary = summaries.find((s) => s.id === identifier);
      if (summary) {
        return summary;
      }
    }
    return null;
  }
}
